import React, { useState, useEffect } from "react";
import { Copy, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

const waLink = (phone, msg) => {
  let d = (phone || "").replace(/\D/g, "");
  if (d.length === 10) d = "91" + d;
  const base = d ? `https://wa.me/${d}` : "https://wa.me/";
  return `${base}?text=${encodeURIComponent(msg)}`;
};

export default function ConfirmationMessageModal({ open, onClose, bookingId, businessId }) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [phone, setPhone] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !bookingId) return;
    setLoading(true);
    setCopied(false);
    setMsg("");
    setPhone("");
    api.get(`/bookings/${bookingId}/confirmation-message`, { params: businessId ? { business_id: businessId } : {} })
      .then((r) => { setMsg(r.data.message || ""); setPhone(r.data.phone || ""); })
      .catch(() => toast.error("Could not build the confirmation message"))
      .finally(() => setLoading(false));
  }, [open, bookingId, businessId]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(msg);
      setCopied(true);
      toast.success("Message copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — please select the text and copy manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-sm:!top-auto max-sm:!bottom-0 max-sm:!translate-y-0 max-sm:!max-w-full max-sm:!rounded-b-none max-sm:!rounded-t-2xl max-h-[90vh] overflow-y-auto rounded-xl sm:max-w-md" data-testid="confirmation-message-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl font-bold text-pulse-navy">
            <MessageCircle size={22} className="text-pulse-green" /> Confirmation message
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate">Auto-filled from this booking. Edit if needed, then copy or share on WhatsApp.</p>

        {loading ? (
          <Skeleton className="mt-3 h-64 w-full rounded-md" />
        ) : (
          <textarea
            data-testid="confirmation-message-text"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={14}
            className="mt-3 w-full resize-y rounded-md border border-slate-200 p-3 text-sm leading-relaxed text-charcoal outline-none focus:border-pulse-green"
          />
        )}

        <div className="mt-4 grid grid-cols-2 gap-3">
          <button type="button" onClick={copy} data-testid="confirmation-copy-btn"
            className="flex items-center justify-center gap-2 rounded-md border border-slate-200 px-4 py-3 text-sm font-semibold text-pulse-navy transition-colors hover:bg-cloud">
            {copied ? <Check size={16} className="text-pulse-green" /> : <Copy size={16} />} {copied ? "Copied" : "Copy"}
          </button>
          <a href={waLink(phone, msg)} target="_blank" rel="noopener noreferrer" data-testid="confirmation-whatsapp-btn"
            className="flex items-center justify-center gap-2 rounded-md bg-[#25D366] px-4 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98]">
            <MessageCircle size={16} /> Share on WhatsApp
          </a>
        </div>
        <button type="button" onClick={onClose} data-testid="confirmation-done-btn"
          className="mt-3 w-full text-center text-xs font-medium text-slate">Done</button>
      </DialogContent>
    </Dialog>
  );
}
